// CategoryTopics.jsx — страница списка тем в категории форума.
// Сверху: backLink + карточка-шапка категории.
// Далее: список карточек тем + пагинация.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import cl from './CategoryTopics.module.css';
import logger from '../../../utils/logger';
import {
    IconArrowLeft, IconMessage, IconUser, IconClock, IconEye,
    IconAlert, IconRefresh, IconClose, IconCheck, IconStar,
} from '../../../components/Icons';

const CategoryTopics = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const [topics, setTopics] = useState([]);
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        loadTopicsAndCategory();
    }, [categoryId, currentPage]);

    const loadTopicsAndCategory = async () => {
        try {
            setLoading(true);
            const [topicsResponse, categoryInfo] = await Promise.all([
                AdminService.getCategoryTopics(categoryId, currentPage, 20),
                AdminService.getForumCategory(categoryId),
            ]);

            setTopics(topicsResponse.data || []);
            setTotalPages(topicsResponse.pages || 1);
            setCategory(categoryInfo);
            setError('');
        } catch (err) {
            setError('Ошибка загрузки данных');
            logger.error('Ошибка загрузки:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTopicClick = (topicId) => {
        navigate(`/main/forum/topics/${topicId}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка тем…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить раздел</h3>
                        <p>{error}</p>
                        <button onClick={loadTopicsAndCategory} className={cl.retryButton}>
                            <IconRefresh width={14} height={14}/>
                            <span>Попробовать снова</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                <button
                    onClick={() => navigate('/main/forum')}
                    className={cl.backLink}
                >
                    <IconArrowLeft width={14} height={14}/>
                    <span>К разделам форума</span>
                </button>

                {/* Шапка категории */}
                <div className={cl.categoryHeader}>
                    <div className={cl.categoryHeaderIcon}>
                        <IconMessage width={22} height={22}/>
                    </div>
                    <div className={cl.categoryHeaderText}>
                        <div className={cl.categoryHeaderTopRow}>
                            <h1 className={cl.categoryTitle}>
                                {category?.name || 'Раздел форума'}
                            </h1>
                            {topics.length > 0 && (
                                <span className={cl.pageHeaderPill}>
                                    <span className={cl.pageHeaderPillStrong}>{topics.length}</span>
                                    <span className={cl.pageHeaderPillMuted}>
                                        {pluralRu(topics.length, 'тема', 'темы', 'тем')}
                                    </span>
                                </span>
                            )}
                        </div>
                        {category?.description && (
                            <p className={cl.categoryDescription}>{category.description}</p>
                        )}
                    </div>
                </div>

                {/* Список тем */}
                {topics.length === 0 ? (
                    <div className={cl.emptyState}>
                        <div className={cl.emptyIconWrap}>
                            <IconMessage width={28} height={28}/>
                        </div>
                        <h2 className={cl.emptyTitle}>В этом разделе пока нет тем</h2>
                        <p className={cl.emptyDescription}>
                            Создайте первую — задайте вопрос с главной страницы форума.
                        </p>
                        <button
                            onClick={() => navigate('/main/forum')}
                            className={cl.createFirstTopic}
                        >
                            <IconArrowLeft width={14} height={14}/>
                            <span>На главную форума</span>
                        </button>
                    </div>
                ) : (
                    <div className={cl.topicsList}>
                        {topics.map(topic => (
                            <TopicCard
                                key={topic.id}
                                topic={topic}
                                onClick={() => handleTopicClick(topic.id)}
                            />
                        ))}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className={cl.pagination}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`${cl.pageButton} ${page === currentPage ? cl.activePage : ''}`}
                            >
                                {page}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ----- Карточка темы -----
const TopicCard = ({ topic, onClick }) => {
    const isClosed = topic.status === 'закрыта';
    return (
        <button
            type="button"
            className={`${cl.topicCard} ${topic.is_pinned ? cl.topicCardPinned : ''}`}
            onClick={onClick}
        >
            <div className={cl.topicCardHead}>
                <h3 className={cl.topicTitle}>
                    {topic.is_pinned && (
                        <span className={cl.pinnedIcon} title="Закреплённая тема">
                            <IconStar width={12} height={12}/>
                        </span>
                    )}
                    <span className={cl.topicTitleText}>{topic.title}</span>
                </h3>
                <span className={`${cl.statusPill} ${isClosed ? cl.statusClosed : cl.statusOpen}`}>
                    {isClosed
                        ? <IconClose width={11} height={11}/>
                        : <IconCheck width={11} height={11}/>}
                    <span>{topic.status}</span>
                </span>
            </div>

            <p className={cl.topicPreview}>
                {AdminService.truncateText(topic.content, 160)}
            </p>

            <div className={cl.topicFooter}>
                <span className={cl.footerItem}>
                    <IconUser width={11} height={11}/>
                    <span className={cl.footerStrong}>{topic.author_name}</span>
                </span>
                <span className={cl.footerItem}>
                    <IconClock width={11} height={11}/>
                    {AdminService.formatForumDate(topic.created_at)}
                </span>
                <span className={cl.footerSpacer}/>
                <span className={cl.footerItem}>
                    <IconEye width={11} height={11}/>
                    {topic.views_count}
                </span>
                <span className={cl.footerItem}>
                    <IconMessage width={11} height={11}/>
                    {topic.replies_count}
                </span>
            </div>

            {topic.last_reply_at && topic.last_reply_author && (
                <div className={cl.lastReply}>
                    <span className={cl.lastReplyLabel}>Последний ответ:</span>
                    <span className={cl.lastReplyAuthor}>{topic.last_reply_author}</span>
                    <span className={cl.lastReplyTime}>
                        · {AdminService.formatForumDate(topic.last_reply_at)}
                    </span>
                </div>
            )}
        </button>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default CategoryTopics;
